import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trophy, Gift, Crown } from "lucide-react";
import { api } from "@/lib/api";
import { useTalk, TkAvatar, tkErr } from "./TalkLayout";

function useCountdown(end) {
  const [, t] = useState(0);
  useEffect(() => { const i = setInterval(() => t((x) => x + 1), 60000); return () => clearInterval(i); }, []);
  if (!end) return "";
  const s = Math.max(0, (new Date(end) - Date.now()) / 1000);
  return `${Math.floor(s / 86400)}d ${Math.floor((s % 86400) / 3600)}h`;
}

const MEDAL = ["#FFE300", "#C9D1E0", "#FF9A4D"];

export default function TalkTop() {
  const { me, reloadMe } = useTalk();
  const [week, setWeek] = useState("current");
  const [data, setData] = useState(null);
  const nav = useNavigate();
  const load = () => api.get("/talk/leaderboard", { params: { week } }).then((r) => setData(r.data)).catch(() => setData({ board: [] }));
  useEffect(() => { setData(null); load(); }, [week]); // eslint-disable-line react-hooks/exhaustive-deps
  const left = useCountdown(week === "current" ? data?.week_end : null);
  const optIn = async (on) => {
    try { await api.patch("/talk/me", { leaderboard_opt_in: on }); await reloadMe(); load(); toast(on ? "You're on the leaderboard" : "You're hidden from the leaderboard"); } catch (e) { tkErr(e); }
  };
  const gift = async (u) => {
    if (u.user_id === me.profile.user_id) return;
    try { const { data: d } = await api.post("/talk/chats", { user_id: u.user_id }); nav(`/talk/c/${d.chat.id}?gift=1`); } catch (e) { tkErr(e); }
  };
  const opted = me.profile.leaderboard_opt_in;
  return (
    <div data-testid="talk-top">
      <div className="flex items-end justify-between">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-[#FFE300]">Weekly gift leaderboard</div>
          <h1 className="font-heading font-black text-4xl">Top <span className="text-[#FFE300]">gifted</span></h1>
        </div>
        <Trophy className="w-10 h-10 text-[#FFE300]" />
      </div>
      <div className="flex items-center gap-2 mt-4">
        <div className="flex rounded-full bg-white/5 p-1 text-xs font-bold">
          {[["current", "This week"], ["last", "Last week"]].map(([k, l]) => <button key={k} onClick={() => setWeek(k)} data-testid={`talk-top-week-${k}`} className={`px-3 py-1.5 rounded-full transition-colors ${week === k ? "bg-[#FFE300] text-black" : "text-white/60"}`}>{l}</button>)}
        </div>
        {week === "current" && left && <span className="text-xs text-white/50 mono" data-testid="talk-top-reset">resets in {left}</span>}
      </div>
      <div className={`mt-5 rounded-3xl p-4 border ${opted ? "border-white/10 bg-white/[0.03]" : "border-[#FFE300]/30 bg-[#FFE300]/5"}`} data-testid="talk-top-optin">
        {opted ? (
          <div className="flex items-center gap-3 text-sm">
            <span className="flex-1">{data?.me ? <>You're <b className="text-[#FFE300]">#{data.me.rank}</b> with {data.me.coins} coins in gifts</> : "You're on the board — receive gifts to climb."}</span>
            <button onClick={() => optIn(false)} className="text-xs text-white/50 underline" data-testid="talk-top-optout-btn">Hide me</button>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <div className="flex-1 text-sm"><b>Want to be ranked?</b><div className="text-xs text-white/50">Only members who opt in appear here. Your gift totals become public each week.</div></div>
            <button onClick={() => optIn(true)} className="bg-[#FFE300] text-black font-black rounded-full px-4 py-2 text-sm" data-testid="talk-top-optin-btn">Join</button>
          </div>
        )}
      </div>
      <div className="mt-5 space-y-2">
        {!data ? <div className="text-white/40 text-sm animate-pulse">Loading…</div> : !data.board.length ? (
          <div className="py-12 text-center text-white/50 text-sm" data-testid="talk-top-empty">No gifts yet this week. Send a gift from any chat to put a friend on the board.</div>
        ) : data.board.map((b) => (
          <div key={b.user.user_id} className={`flex items-center gap-3 p-3 rounded-2xl ${b.rank <= 3 ? "bg-gradient-to-r from-[#FFE300]/10 to-transparent border border-[#FFE300]/20" : "bg-white/[0.03]"}`} data-testid={`talk-top-row-${b.user.username}`}>
            <span className="w-8 text-center font-heading font-black text-xl" style={{ color: MEDAL[b.rank - 1] || "rgba(255,255,255,.5)" }}>{b.rank === 1 ? <Crown className="w-6 h-6 mx-auto" /> : b.rank}</span>
            <TkAvatar p={b.user} size={44} />
            <div className="flex-1 min-w-0">
              <div className="font-bold truncate">{b.user.display_name}</div>
              <div className="text-[11px] text-white/50 mono">{b.gifts} {b.gifts === 1 ? "gift" : "gifts"} · {b.fans} {b.fans === 1 ? "fan" : "fans"}</div>
            </div>
            <span className="mono font-black text-[#FFE300]" data-testid={`talk-top-coins-${b.user.username}`}>{b.coins}</span>
            {b.user.user_id !== me.profile.user_id && <button onClick={() => gift(b.user)} aria-label="Send gift" data-testid={`talk-top-gift-${b.user.username}`} className="w-9 h-9 rounded-full bg-[#FFE300]/15 text-[#FFE300] flex items-center justify-center"><Gift className="w-4 h-4" /></button>}
          </div>
        ))}
      </div>
    </div>
  );
}
